import { useState, useEffect, useCallback, useRef } from 'react';
import { otaApi } from '../api/ota';
import { mqttOtaApi } from '../api/mqttOta';
import { useI18n, translate } from '../i18n';
import type { TranslationKey } from '../i18n/translations';

type ServiceType = 'ocpp' | 'mqtt';

interface UnifiedDevice {
  device_sn: string;
  label: string;
  service: ServiceType;
  online: boolean;
  fw_version: string | null;
}

interface UnifiedUpdate {
  id: number;
  device_sn: string;
  status: string;
  service: ServiceType;
  created_at: string;
  updated_at: string;
  firmware_url?: string;
}

const STATUS_KEYS: Record<string, TranslationKey> = {
  completed: 'adm_ota_status_completed',
  installed: 'adm_ota_status_installed',
  in_progress: 'adm_ota_status_in_progress',
  downloading: 'adm_ota_status_downloading',
  sent: 'adm_ota_status_sent',
  pending: 'adm_ota_status_pending',
  failed: 'adm_ota_status_failed',
};

function StatusBadge({ status }: { status: string }) {
  const { t } = useI18n();
  const styles: Record<string, string> = {
    completed: 'bg-green-100 text-green-700 border-green-300',
    installed: 'bg-green-100 text-green-700 border-green-300',
    in_progress: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    downloading: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    sent: 'bg-blue-100 text-blue-700 border-blue-300',
    pending: 'bg-blue-100 text-blue-700 border-blue-300',
    failed: 'bg-red-100 text-red-700 border-red-300',
  };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${styles[status] || 'bg-gray-100 text-gray-600 border-gray-300'}`}>
      {STATUS_KEYS[status] ? t(STATUS_KEYS[status]).toUpperCase() : status?.toUpperCase() || t('adm_ota_status_unknown')}
    </span>
  );
}

function ServiceBadge({ service }: { service: ServiceType }) {
  return service === 'mqtt' ? (
    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-teal-100 text-teal-700 border border-teal-300">MQTT</span>
  ) : (
    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-purple-100 text-purple-700 border border-purple-300">OCPP</span>
  );
}

function formatDate(d: string | null, locale: string) {
  if (!d) return '-';
  try {
    const dt = new Date(d);
    return dt.toLocaleString(locale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return d;
  }
}

export default function OtaPage() {
  const { t, locale } = useI18n();
  const [updates, setUpdates] = useState<UnifiedUpdate[]>([]);
  const [devices, setDevices] = useState<UnifiedDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Trigger form
  const [selectedDevice, setSelectedDevice] = useState('');
  const [triggering, setTriggering] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // File upload
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [firmwareUrl, setFirmwareUrl] = useState<string | null>(null);

  // Filters
  const [filterService, setFilterService] = useState<'all' | ServiceType>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterDevice, setFilterDevice] = useState('');

  const loadData = useCallback(async () => {
    try {
      // Load from both services in parallel
      const [ocppHistory, ocppDevices, mqttHistory, mqttDevices] = await Promise.allSettled([
        otaApi.getHistory(),
        otaApi.getDevices(),
        mqttOtaApi.getHistory(),
        mqttOtaApi.getDevices(),
      ]);

      // Merge devices
      const mergedDevices: UnifiedDevice[] = [];
      if (ocppDevices.status === 'fulfilled') {
        for (const d of ocppDevices.value.data) {
          mergedDevices.push({
            device_sn: d.device_sn,
            label: d.device_name || d.device_sn,
            service: 'ocpp',
            online: d.device_status === 'online',
            fw_version: d.firmware_version,
          });
        }
      }
      if (mqttDevices.status === 'fulfilled') {
        for (const d of mqttDevices.value.data) {
          mergedDevices.push({
            device_sn: d.device_sn,
            label: d.device_sn,
            service: 'mqtt',
            online: d.ws_connected || d.online,
            fw_version: d.fw_version,
          });
        }
      }

      // Merge history
      const mergedUpdates: UnifiedUpdate[] = [];
      if (ocppHistory.status === 'fulfilled') {
        for (const u of ocppHistory.value.data) {
          mergedUpdates.push({
            id: u.id,
            device_sn: u.device_sn || translate('pg_ota_device_fallback', { id: u.device_id }),
            status: u.status,
            service: 'ocpp',
            created_at: u.created_at,
            updated_at: u.updated_at,
            firmware_url: u.firmware_url,
          });
        }
      }
      if (mqttHistory.status === 'fulfilled') {
        for (const u of mqttHistory.value.data) {
          mergedUpdates.push({
            id: u.id,
            device_sn: u.device_sn,
            status: u.status,
            service: 'mqtt',
            created_at: u.sent_at,
            updated_at: u.acked_at || u.sent_at,
            firmware_url: u.firmware_url,
          });
        }
      }

      // Sort by date descending
      mergedUpdates.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      setDevices(mergedDevices);
      setUpdates(mergedUpdates);
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : translate('adm_ota_err_load'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 15000);
    return () => clearInterval(interval);
  }, [loadData]);

  const getSelectedDeviceService = (): ServiceType | null => {
    const dev = devices.find(d => d.device_sn === selectedDevice);
    return dev?.service || null;
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setMessage(null);
    try {
      const service = getSelectedDeviceService();
      const res = service === 'mqtt'
        ? await mqttOtaApi.uploadFirmware(selectedFile)
        : await otaApi.uploadFirmware(selectedFile);
      if (res.success && res.data) {
        setFirmwareUrl(res.data.url);
        setMessage({ type: 'success', text: `✅ ${t('adm_ota_uploaded', { name: res.data.filename, size: (res.data.size / 1024).toFixed(0) })}` });
      } else {
        setMessage({ type: 'error', text: `❌ ${res.error || t('adm_ota_err_upload')}` });
      }
    } catch (err: unknown) {
      setMessage({ type: 'error', text: `❌ ${err instanceof Error ? err.message : t('adm_ota_err_upload_network')}` });
    } finally {
      setUploading(false);
    }
  };

  const handleTrigger = async () => {
    if (!selectedDevice || !firmwareUrl) return;
    setTriggering(true);
    setMessage(null);

    try {
      const service = getSelectedDeviceService();
      if (service === 'mqtt') {
        const res = await mqttOtaApi.triggerOta(selectedDevice, firmwareUrl);
        if (res.success) {
          setMessage({ type: 'success', text: `✅ ${res.message} (${t('adm_ota_mqtt_sent', { id: String(res.data?.ota_id), delivered: String(res.data?.delivered) })})` });
          loadData();
        } else {
          setMessage({ type: 'error', text: `❌ ${res.error || t('pg_ota_err_trigger')}` });
        }
      } else {
        const res = await otaApi.triggerOta(selectedDevice, firmwareUrl);
        if (res.success) {
          setMessage({ type: 'success', text: `✅ ${res.message} (ID: ${res.data?.firmware_update_id})` });
          loadData();
        } else {
          setMessage({ type: 'error', text: `❌ ${res.error || t('pg_ota_err_trigger')}` });
        }
      }
    } catch (err: unknown) {
      setMessage({ type: 'error', text: `❌ ${err instanceof Error ? err.message : t('adm_ota_err_network')}` });
    } finally {
      setTriggering(false);
    }
  };

  // Filtered updates
  const filteredUpdates = updates.filter(u => {
    if (filterService !== 'all' && u.service !== filterService) return false;
    if (filterStatus !== 'all' && u.status !== filterStatus) return false;
    if (filterDevice && !u.device_sn.toLowerCase().includes(filterDevice.toLowerCase())) return false;
    return true;
  });

  // Stats
  const stats = {
    completed: updates.filter(u => u.status === 'completed' || u.status === 'installed').length,
    inProgress: updates.filter(u => u.status === 'in_progress' || u.status === 'downloading' || u.status === 'sent').length,
    pending: updates.filter(u => u.status === 'pending').length,
    failed: updates.filter(u => u.status === 'failed').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
          <button onClick={loadData} className="ml-4 underline">{t('adm_ota_retry')}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">🔧 {t('adm_ota_title')}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {t('adm_ota_subtitle', { n: updates.length, done: stats.completed })}
          </p>
        </div>
        <button
          onClick={loadData}
          className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          ↻ {t('adm_ota_refresh')}
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-green-100 border border-green-300 rounded-xl p-4 shadow-sm">
          <div className="text-3xl font-extrabold text-green-700">{stats.completed}</div>
          <div className="text-sm font-semibold text-green-700">{t('adm_ota_stat_completed')}</div>
        </div>
        <div className="bg-yellow-100 border border-yellow-300 rounded-xl p-4 shadow-sm">
          <div className="text-3xl font-extrabold text-yellow-700">{stats.inProgress}</div>
          <div className="text-sm font-semibold text-yellow-700">{t('adm_ota_stat_in_progress')}</div>
        </div>
        <div className="bg-blue-100 border border-blue-300 rounded-xl p-4 shadow-sm">
          <div className="text-3xl font-extrabold text-blue-700">{stats.pending}</div>
          <div className="text-sm font-semibold text-blue-700">{t('adm_ota_stat_pending')}</div>
        </div>
        <div className="bg-red-100 border border-red-300 rounded-xl p-4 shadow-sm">
          <div className="text-3xl font-extrabold text-red-700">{stats.failed}</div>
          <div className="text-sm font-semibold text-red-700">{t('adm_ota_stat_failed')}</div>
        </div>
      </div>

      {/* ─── Trigger OTA Card ─── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">🚀 {t('adm_ota_trigger_title')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
          {/* Device selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('adm_ota_devices')}</label>
            <select
              value={selectedDevice}
              onChange={(e) => setSelectedDevice(e.target.value)}
              className="w-full rounded-lg border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm p-2 border"
            >
              <option value="">{t('adm_ota_select_device')}</option>
              {devices.filter(d => d.service === 'mqtt').length > 0 && (
                <optgroup label={`🌐 ${t('adm_ota_group_mqtt')}`}>
                  {devices.filter(d => d.service === 'mqtt').map((d) => (
                    <option key={d.device_sn} value={d.device_sn}>
                      {d.device_sn} {d.online ? '🟢' : '🔴'} (fw: {d.fw_version || '?'})
                    </option>
                  ))}
                </optgroup>
              )}
              {devices.filter(d => d.service === 'ocpp').length > 0 && (
                <optgroup label={`⚡ ${t('adm_ota_group_ocpp')}`}>
                  {devices.filter(d => d.service === 'ocpp').map((d) => (
                    <option key={d.device_sn} value={d.device_sn}>
                      {d.label} {d.online ? '🟢' : '🔴'} (fw: {d.fw_version || '?'})
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>

          {/* File picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('pg_ota_firmware_bin')}</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".bin"
              onChange={(e) => {
                setSelectedFile(e.target.files?.[0] || null);
                setFirmwareUrl(null);
              }}
              className="w-full text-sm text-gray-500 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 cursor-pointer"
            />
          </div>

          {/* Upload button */}
          <div>
            <button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              className="w-full px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
            >
              {uploading ? `⏳ ${t('adm_ota_uploading')}` : `☁ ${t('adm_ota_upload_gcs')}`}
            </button>
          </div>

          {/* Trigger button */}
          <div>
            <button
              onClick={handleTrigger}
              disabled={!selectedDevice || !firmwareUrl || triggering}
              className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium"
            >
              {triggering ? `⏳ ${t('adm_ota_sending')}` : `⚡ ${t('adm_ota_trigger')}`}
            </button>
          </div>
        </div>

        {/* Uploaded URL */}
        {firmwareUrl && (
          <div className="mt-3 p-2 bg-gray-50 rounded-lg border text-xs text-gray-600 truncate">
            📦 <span className="font-mono">{firmwareUrl}</span>
          </div>
        )}

        {/* Message */}
        {message && (
          <div className={`mt-3 p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            {message.text}
          </div>
        )}
      </div>

      {/* ─── Firmware Updates Table ─── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-lg font-semibold text-gray-900">
            📋 {t('adm_ota_history', { shown: filteredUpdates.length, total: updates.length })}
          </h2>
          <div className="flex items-center gap-3">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="text-xs border rounded-lg px-2 py-1"
            >
              <option value="all">{t('adm_ota_all_statuses', { n: updates.length })}</option>
              <option value="completed">{t('adm_ota_status_completed')}</option>
              <option value="installed">{t('adm_ota_status_installed')}</option>
              <option value="sent">{t('adm_ota_status_sent')}</option>
              <option value="pending">{t('adm_ota_status_pending')}</option>
              <option value="in_progress">{t('adm_ota_status_in_progress')}</option>
              <option value="failed">{t('adm_ota_status_failed')}</option>
            </select>
            <select
              value={filterService}
              onChange={(e) => setFilterService(e.target.value as 'all' | ServiceType)}
              className="text-xs border rounded-lg px-2 py-1"
            >
              <option value="all">{t('adm_ota_all_services')}</option>
              <option value="mqtt">{t('adm_ota_mqtt_only')}</option>
              <option value="ocpp">{t('adm_ota_ocpp_only')}</option>
            </select>
            <input
              type="text"
              value={filterDevice}
              onChange={(e) => setFilterDevice(e.target.value)}
              placeholder={`🔍 ${t('adm_ota_filter_device')}`}
              className="text-xs border rounded-lg px-2 py-1 w-36"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('adm_ota_th_device')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('adm_ota_th_status')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('adm_ota_th_created')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('adm_ota_th_updated')}</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredUpdates.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-400">
                    {t('adm_ota_empty')}
                  </td>
                </tr>
              ) : (
                filteredUpdates.map((u) => (
                  <tr key={`${u.service}-${u.id}`} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-3 text-sm font-mono text-gray-900">#{u.id}</td>
                    <td className="px-6 py-3 text-sm">
                      <div className="font-medium text-gray-900">{u.device_sn}</div>
                      <div className="text-xs text-gray-400 mt-0.5"><ServiceBadge service={u.service} /></div>
                    </td>
                    <td className="px-6 py-3"><StatusBadge status={u.status} /></td>
                    <td className="px-6 py-3 text-sm text-gray-500">{formatDate(u.created_at, locale)}</td>
                    <td className="px-6 py-3 text-sm text-gray-500">{formatDate(u.updated_at, locale)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
